import { useEffect, useRef } from 'react';
import { lloydConstants } from './Constants';
import { getOptionsFromObject } from './utils';
import { readUrlState, writeUrlState } from './urlState';
import {
	NO_COMPARISON,
	useFilterContext,
	useSelectionContext,
	useWeightContext,
	useYearContext,
} from './context';

// Option lists keyed the way the query string stores them, so a shared link
// restores the same chips rather than raw ids.
const optionLookup = (source) => {
	const options = getOptionsFromObject(source);
	return (id) => options.find((option) => String(option.id) === String(id));
};

const countryOption = (name) => {
	const props = lloydConstants.countriesProps[name];
	return props ? { id: name, label: name, group: props.region } : undefined;
};

const decode = (raw, toOption) =>
	(raw ?? '')
		.split('.')
		.filter(Boolean)
		.map(toOption)
		.filter(Boolean);

/**
 * Keeps the whole view in the query string. Without this the tool can only be
 * shared as a screenshot, which strips the filters and weighting that produced
 * the ranking someone is pointing at.
 */
export const useUrlState = () => {
	const filters = useFilterContext();
	const selection = useSelectionContext();
	const weights = useWeightContext();
	const year = useYearContext();
	const restored = useRef(false);

	useEffect(() => {
		if (restored.current) return;
		restored.current = true;

		const state = readUrlState();

		if (state.year && lloydConstants.waves.includes(state.year)) year.setSelectedYear(state.year);
		if (state.comparisonYear !== undefined) {
			const valid =
				state.comparisonYear === NO_COMPARISON || lloydConstants.waves.includes(state.comparisonYear);
			if (valid) year.setComparisonYear(state.comparisonYear);
		}

		const lists = [
			['gender', lloydConstants.genders, filters.setGender],
			['ageGroup', lloydConstants.ageGroups, filters.setAgeGroup],
			['educationLevel', lloydConstants.educationLevels, filters.setEducationLevel],
			['income', lloydConstants.incomeQuintiles, filters.setIncome],
			['employmentStatus', lloydConstants.employmentStatus, filters.setEmploymentStatus],
			['urbanicity', lloydConstants.urbanicity, filters.setUrbanicity],
		];

		lists.forEach(([key, source, setter]) => {
			if (state[key] !== undefined) setter(decode(state[key], optionLookup(source)));
		});

		if (state.incomeLevel !== undefined) {
			selection.setIncomeLevel(decode(state.incomeLevel, optionLookup(lloydConstants.incomeLevels)));
		}

		if (state.country !== undefined) {
			selection.setCountry(decode(state.country, countryOption));
		}

		if (state.weights) {
			weights.setIndividual(state.weights.individual);
			weights.setHousehold(state.weights.household);
			weights.setCommunity(state.weights.community);
			weights.setSociety(state.weights.society);
		}

		if (state.colorBy) selection.setColorBy(state.colorBy);
		if (state.colourBlindSafe) selection.setColourBlindSafe(true);
		// Restore runs once, on mount, from the URL the visitor arrived with.
	}, []);

	useEffect(() => {
		if (!restored.current) return;

		writeUrlState({
			year: year.selectedYear,
			comparisonYear: year.comparisonYear,
			filters,
			selection,
			weights,
			colourBlindSafe: selection.colourBlindSafe,
		});
	}, [filters, selection, weights, year.selectedYear, year.comparisonYear]);
};
